import React, { useContext, useState } from "react";
import { useForm } from "react-hook-form";

import { Checkbox, FormControlLabel, InputAdornment } from "@material-ui/core";
import { useTranslation } from "react-i18next";

import { bright_90, increaseBrightness } from "common/helpers/adjustColor";
import { getFooterLinks } from "common/helpers/getFooterLinks";
import ValidationHelper from "common/helpers/validation.helper";
import upsdkService from "common/services/upsdkService";
import configSelector from "common/store/selectors/configSelector";
import upsdkSelector from "common/store/selectors/upsdkSelector";

import CheckedCheckboxIcon from "../../../assets/icons/checked-checkbox.icon";
import MailIcon from "../../../assets/icons/mail.icon";
import PhoneIcon from "../../../assets/icons/phone.icon";
import ProfileIcon from "../../../assets/icons/profile.icon";
import UncheckedCheckboxIcon from "../../../assets/icons/unchecked-checkbox.icon";
import AppButton from "../../../common/button/index.component";
import InputField from "../../../common/fields/input-field/index.component";
import ServerError from "../../../common/server-error/index.component";
import Typography from "../../../common/typography/index.component";
import getCustomPageLinksForAuth from "../../../helpers/getCustomPageLinks";
import { COLOR_DARK_LIVER } from "../../../constants/colors.constants";
import CountryHelper from "../../../helpers/countryCode.helper";
import MiscHelper from "common/helpers/misc.helper";
import {
  FORMAT_TYPE_SVG,
  FORMAT_TYPE_UNICODE,
} from "../../../constants/enums.constants";
import useRouteHook from "../../../hooks/useRoute.hook";
import { BaseContext } from "../../../context/BaseContext";

import "./index.component.scss";

export default function SignUp({
  config,
  primaryTextColor,
  secondaryColor,
  primaryColor,
  tertiaryColor,
  upsdk,
  handleSetView,
  setAuthResponse,
  setActiveAuthFlow,
  formData,
  setFormData,
  primaryLogin,
}) {
  const { isQrModeEnabled } = useContext(BaseContext);
  const [loading, setLoading] = useState(false);
  const [isOptForPromo, setOptForPromo] = useState(false);
  const [isTermAgree, setTermAgree] = useState(isQrModeEnabled);
  const { register, handleSubmit, errors, setError } = useForm({
    mode: "onBlur",
    defaultValues: {
      phone: formData.phone,
      fullName: formData.fullName,
      email: formData.email,
    },
  });
  const [signUpError, setSignUpError] = useState(undefined);
  const { CustomLink } = useRouteHook();

  // variables
  const { t } = useTranslation();
  const footerLinks = getFooterLinks(configSelector.getCustomPages({ config }));
  const customPageLinks = getCustomPageLinksForAuth(footerLinks);
  const isd_code = upsdkSelector.getISDCode({ upsdk });
  const currency = upsdkSelector.getCurrency({ upsdk });

  const inputChangeHandler = (event) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  const onSubmit = (data) => {
    setActiveAuthFlow("signup");
    const { phone, email, fullName } = data;

    if (!isNaN(fullName)) {
      return setError("fullName", {
        type: "custom",
        message: t("common.validUserNameHelperText"),
      });
    }

    let fullNameSplit = fullName.split(" ");
    const [first_name, ...rest] = fullNameSplit;
    const last_name = rest.join(" ");

    setLoading(true);

    const referralData = JSON.parse(localStorage.getItem("branch_ref_data"));

    let referralParams = {};

    if (referralData) {
      if (referralData["~referring_link"]) {
        referralParams.code_link = referralData["~referring_link"];
      }

      if (referralData.card) {
        referralParams.referrer_card = referralData.card;
      }

      if (referralData["~channel"]) {
        referralParams.channel = referralData["~channel"];
      }

      if (referralData.link_share_time) {
        referralParams.shared_on = referralData.link_share_time;
      }

      if (referralData.share_platform) {
        referralParams.platform = referralData.share_platform;
      }
    }

    upsdkService
      .signup({
        isd_code,
        first_name,
        last_name,
        phone,
        email,
        receive_promo: isOptForPromo,
        referralData: referralParams,
      })
      .then((response) => {
        setSignUpError(null);
        handleSetView("otpVerify");
        setAuthResponse({
          isd_code: isd_code,
          customer_phone: phone,
          customer_email: email,
          ...response,
        });
      })
      .catch((error) => {
        setSignUpError(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleChange = (event) => {
    setTermAgree(event.target.checked);
    setSignUpError(undefined);
  };

  const handlePromoChange = (event) => {
    setOptForPromo(event.target.checked);
  };

  return (
    <div className="sign-up-form">
      <div className="dialog-content">
        <form className="form-content" onSubmit={handleSubmit(onSubmit)}>
          <div className="form-row">
            <InputField
              type="number"
              errors={errors}
              onChange={inputChangeHandler}
              name="phone"
              className="full-width"
              refForm={register(ValidationHelper.phoneNumber(t, isd_code))}
              label={
                <Typography
                  variant="h4"
                  weight="bold"
                  fontColor={primaryTextColor}
                  isRequired={true}
                >
                  {t("authDialog.phoneNumber")}
                </Typography>
              }
              inputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {MiscHelper.isWindowsOS() ? (
                      <img
                        height="16px"
                        width="16px"
                        src={CountryHelper.getCountryFlag(
                          isd_code,
                          currency,
                          FORMAT_TYPE_SVG,
                        )}
                        alt="country-flag"
                      />
                    ) : (
                      CountryHelper.getCountryFlag(
                        isd_code,
                        currency,
                        FORMAT_TYPE_UNICODE,
                      )
                    )}
                    &nbsp;
                    {isd_code}
                  </InputAdornment>
                ),
              }}
              helperText={errors.phone && errors.phone.message}
              inputIcon={<PhoneIcon className="field-icon" />}
              primaryTextColor={primaryTextColor}
              iconBackgroundColor={increaseBrightness(primaryColor, bright_90)}
            />
          </div>
          <div className="form-row">
            <InputField
              type="text"
              errors={errors}
              onChange={inputChangeHandler}
              name="fullName"
              className="full-width"
              refForm={register(ValidationHelper.userName(t))}
              label={
                <Typography
                  variant="h4"
                  weight="bold"
                  fontColor={primaryTextColor}
                  isRequired={true}
                >
                  {t("authDialog.fullName")}
                </Typography>
              }
              helperText={errors.fullName && errors.fullName.message}
              inputIcon={<ProfileIcon className="field-icon" />}
              primaryTextColor={primaryTextColor}
              iconBackgroundColor={increaseBrightness(
                secondaryColor,
                bright_90,
              )}
            />
          </div>
          <div className="form-row">
            <InputField
              type="email"
              name="email"
              errors={errors}
              onChange={inputChangeHandler}
              className="full-width"
              refForm={register(ValidationHelper.email(t))}
              label={
                <Typography
                  variant="h4"
                  weight="bold"
                  fontColor={primaryTextColor}
                  isRequired={true}
                >
                  {t("authDialog.emailAddress")}
                </Typography>
              }
              helperText="Please provide valid email address"
              inputIcon={<MailIcon fill="#000000" className="field-icon" />}
              primaryTextColor={primaryTextColor}
              iconBackgroundColor={increaseBrightness(tertiaryColor, bright_90)}
            />
          </div>
        </form>
        <React.Fragment>
          <div className="terms-check-wrapper">
            <FormControlLabel
              classes={{ root: "terms-check" }}
              control={
                <Checkbox
                  icon={<UncheckedCheckboxIcon className="uncheck-icon" />}
                  checkedIcon={<CheckedCheckboxIcon className="check-icon" />}
                  onChange={handleChange}
                  name="terms-check"
                />
              }
              label={
                <Typography
                  variant="h3"
                  weight="regular"
                  fontColor={COLOR_DARK_LIVER}
                  className="terms-check-label"
                >
                  {t("authDialog.iAgreeTo")}
                  <CustomLink
                    to={
                      customPageLinks.terms ||
                      "https://www.urbanpiper.com/legal/customer-terms-and-conditions"
                    }
                    target="_blank"
                  >
                    <Typography
                      variant="h3"
                      weight="regular"
                      fontColor={primaryColor}
                    >
                      {t("authDialog.terms")}
                    </Typography>
                  </CustomLink>{" "}
                  {t("authDialog.and")}
                  <CustomLink
                    to={
                      customPageLinks.policy ||
                      "https://www.urbanpiper.com/legal/privacy-policy"
                    }
                    target="_blank"
                  >
                    <Typography
                      variant="h3"
                      weight="regular"
                      fontColor={primaryColor}
                    >
                      {t("authDialog.privacy&Policy")}
                    </Typography>
                  </CustomLink>
                </Typography>
              }
            />
          </div>
          <div className="terms-check-wrapper" style={{ marginBottom: 24 }}>
            <FormControlLabel
              classes={{ root: "terms-check" }}
              control={
                <Checkbox
                  icon={<UncheckedCheckboxIcon className="uncheck-icon" />}
                  checkedIcon={<CheckedCheckboxIcon className="check-icon" />}
                  onChange={handlePromoChange}
                  name="terms-check"
                />
              }
              label={
                <Typography
                  variant="h3"
                  weight="regular"
                  fontColor={COLOR_DARK_LIVER}
                  className="terms-check-label"
                >
                  {t("authDialog.promoMessage")}
                </Typography>
              }
            />
          </div>
        </React.Fragment>

        {signUpError && <ServerError message={signUpError} />}

        <AppButton
          fullWidth
          variant="contained"
          className="sign-up-button"
          isDisabled={!isTermAgree}
          isLoading={loading}
          buttonColor={primaryColor}
          onClickCallback={handleSubmit(onSubmit)}
        >
          <Typography
            variant="h2"
            weight="bold"
            className="sign-up-button-text"
          >
            {t("authDialog.continue")}
          </Typography>
        </AppButton>
      </div>
    </div>
  );
}
